import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { Modal } from 'ui-components/components/modal';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { LabeledInfoList } from 'ui-components/components/labeled-info-list';

import OrderNumbers from './details/order-numbers';
import ShipmentIds from './details/shipment-ids';
import TrackingLinks from './details/tracking-data';
import DateTime from './details/date-time';
import Carriers from './details/carrier';
import { CurrencyPropType } from '../../../../prop-types/currency';
import { MessageDescriptorPropType } from '../../../../prop-types/intl';

import ItemBreakdown from './item-breakdown';

import messages from '../messages';
import styles from './styles.module.less';
import InsuranceProviders from './details/insurance-providers';
import AdjustmentId from './details/adjustment-id';
import ModalPortal from '../../../../components/modal-portal';
import { AllTransactionTypes, AdjustmentTransactionType } from '../constants';
import { AdjustmentBreakdown } from './item-breakdown/adjustment-breakdown';

// The order of this component mapping dictates the order of the details in the modal
const detailComponentMap = {
  dateTime: DateTime,
  fulfillments: ShipmentIds,
  trackingLinks: TrackingLinks,
  salesOrders: OrderNumbers,
  carrierIds: Carriers,
  insuranceProviderIds: InsuranceProviders,
  adjustmentId: AdjustmentId,
};

const TransactionDetailsModal = ({ data, close }) => {
  if (!data) {
    return null;
  }

  const { transactionTotal, transactionType, lineItems, ...details } = data;

  return (
    <ModalPortal onClose={close}>
      <Modal size='medium'>
        <ModalHeader>
          <span>
            <FormattedMessage {...messages.transactionDetails} />
          </span>
        </ModalHeader>
        <ModalBody className={styles.body}>
          <LabeledInfoList className={styles['details-section']}>
            {Object.entries(detailComponentMap)
              .filter(([detailKey]) => details?.[detailKey])
              .map(([detailKey, DetailComponent]) => (
                <DetailComponent key={detailKey} data={details[detailKey]} />
              ))}
          </LabeledInfoList>
          {transactionType === AdjustmentTransactionType ? (
            <AdjustmentBreakdown adjustmentData={data} />
          ) : (
            <ItemBreakdown lineItems={lineItems} transactionTotal={transactionTotal} transactionType={transactionType} />
          )}
        </ModalBody>
      </Modal>
    </ModalPortal>
  );
};

TransactionDetailsModal.propTypes = {
  data: PropTypes.shape({
    transactionTotal: CurrencyPropType,
    transactionType: PropTypes.oneOf(AllTransactionTypes),
    lineItems: PropTypes.arrayOf(
      PropTypes.shape({
        amount: CurrencyPropType,
        description: PropTypes.oneOfType([PropTypes.string, MessageDescriptorPropType]),
      }),
    ),
  }).isRequired,
  close: PropTypes.func.isRequired,
};

export default TransactionDetailsModal;
